import { useState, useMemo, useEffect, useRef } from "react";
import { formatCurrency, formatWhatsApp, rotuloDaModalidade, waPhone } from "@/lib/utils";
import { abreWhatsApp } from "@/lib/abreWhatsApp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Loader2, Clock, User, UserRound, CheckCircle, XCircle, AlertTriangle, Filter, X, Copy, ExternalLink, Package } from "lucide-react";
import { useLocation } from "wouter";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { PublicLinkCard } from "@/components/PublicLinkCard";
import { customFetch, getGetFinancialSummaryQueryKey, getListTransactionsQueryKey } from "@workspace/api-client-react";
import { extractApiErrorMessage } from "@/lib/api-error";
import { NOME_DO_TURNO, ehTurno, horarioDosTurnos, ordemNoDia } from "@workspace/pricing";
import { useQuery, useMutation } from "@tanstack/react-query";

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

interface Appointment {
  id: string;
  date: string;
  time: string;
  durationMinutes: number;
  status: string;
  notes: string | null;
  professionalId: string | null;
  type: string | null;
  customer: { id: string; firstName: string; whatsappNumber: string; avatarBase64: string | null } | null;
  service: { id: string; name: string; price: string; durationMinutes: number } | null;
  professional: { id: string; name: string; phone: string | null; avatarUrl: string | null } | null;
}

interface Service {
  id: string;
  name: string;
  price: string;
  durationMinutes: number;
}

interface Professional {
  id: string;
  name: string;
  isActive: boolean;
  specialtySchedules: Record<string, Record<string, { start: string; end: string; enabled: boolean }>> | null;
  // Modelo ATUAL (o que a página Profissionais cria): atende todos os
  // serviços numa grade geral. specialtySchedules é o modelo antigo.
  allServices?: boolean | null;
  generalSchedule?: Record<string, { enabled: boolean; ranges: { start: string; end: string }[] }> | null;
}

// Grade que vale pro serviço, nos dois modelos. Até 08/09/2026 esta modal só
// enxergava o antigo e descartava 9 em 10 profissionais ("Sem profissional").
function scheduleForService(p: Professional, serviceId: string): Record<string, { enabled: boolean }> | null {
  if (p.allServices && p.generalSchedule) return p.generalSchedule;
  return p.specialtySchedules?.[serviceId] ?? null;
}

function useAppointments(dateFrom: string, dateTo: string) {
  return useQuery<Appointment[]>({
    queryKey: ["appointments", dateFrom, dateTo],
    queryFn: () => customFetch(`/api/appointments?dateFrom=${dateFrom}&dateTo=${dateTo}`),
  });
}

interface ScheduledOrderItem {
  itemId: string;
  orderId: string;
  productName: string;
  price: string;
  quantity: string;
  notes: string | null;
  scheduledDate: string;
  scheduledTime: string | null;
  scheduledShift?: string | null;
  optionsTotal: string | null;
  orderStatus: string;
  orderTotal: string;
  deliveryMode: string;
  customer: { id: string; firstName: string; whatsappNumber: string | null; avatarBase64: string | null } | null;
}

function useScheduledOrders(dateFrom: string, dateTo: string) {
  return useQuery<ScheduledOrderItem[]>({
    queryKey: ["scheduled-orders", dateFrom, dateTo],
    queryFn: () => customFetch(`/api/orders/scheduled?dateFrom=${dateFrom}&dateTo=${dateTo}`),
  });
}

function useServicesList() {
  return useQuery<Service[]>({
    queryKey: ["services-list"],
    queryFn: () => customFetch("/api/services?status=ativo"),
  });
}

function useProfessionals() {
  return useQuery<Professional[]>({
    queryKey: ["professionals"],
    queryFn: () => customFetch("/api/professionals"),
  });
}

interface StoreSettings {
  appointmentMessageTemplate: string | null;
}

function useStoreSettings() {
  return useQuery<StoreSettings>({
    queryKey: ["store-settings"],
    queryFn: () => customFetch("/api/settings"),
  });
}

interface CustomerResult {
  id: string;
  firstName: string;
  whatsappNumber: string | null;
}

function useCustomerSearch(search: string) {
  return useQuery<CustomerResult[]>({
    queryKey: ["customers-search", search],
    queryFn: () => customFetch(`/api/customers?search=${encodeURIComponent(search)}`),
    enabled: search.length >= 1,
    staleTime: 30_000,
  });
}

function useAdminSlots(date: string, serviceId: string, professionalId: string) {
  const qs = new URLSearchParams();
  if (date) qs.set("date", date);
  if (serviceId) qs.set("serviceId", serviceId);
  if (professionalId) qs.set("professionalId", professionalId);
  return useQuery<{ slots: string[] }>({
    queryKey: ["admin-slots", date, serviceId, professionalId],
    queryFn: () => customFetch(`/api/appointments/admin-slots?${qs.toString()}`),
    enabled: !!date && !!serviceId,
  });
}

const statusLabels: Record<string, string> = {
  agendado: "Agendado",
  confirmado: "Confirmado",
  em_atendimento: "Em atendimento",
  concluido: "Concluído",
  cancelado: "Cancelado",
  nao_compareceu: "Não compareceu",
};

const statusColors: Record<string, string> = {
  agendado: "bg-blue-100 text-blue-700",
  confirmado: "bg-green-100 text-green-700",
  em_atendimento: "bg-yellow-100 text-yellow-700",
  concluido: "bg-gray-100 text-gray-600",
  cancelado: "bg-red-100 text-red-600",
  nao_compareceu: "bg-orange-100 text-orange-600",
};

function getWeekDates(date: Date) {
  const start = new Date(date);
  const day = start.getDay();
  const diff = (day + 6) % 7; // 0=Mon, 1=Tue, ..., 6=Sun offset from Monday
  start.setDate(start.getDate() - diff);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function formatDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const dayKeys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function Agenda() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(formatDate(new Date()));
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [filterProfessionalId, setFilterProfessionalId] = useState("");
  const [filterServiceId, setFilterServiceId] = useState("");
  const [filterStatus, setFilterStatus] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("status") || "";
  });
  const [showFilters, setShowFilters] = useState(false);

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const dateFrom = formatDate(weekDates[0]!);
  const dateTo = formatDate(weekDates[6]!);

  const { data: appointments = [], isLoading } = useAppointments(dateFrom, dateTo);
  const { data: scheduledOrders = [] } = useScheduledOrders(dateFrom, dateTo);
  const { data: services = [] } = useServicesList();
  const { data: professionals = [] } = useProfessionals();
  const { data: storeSettings } = useStoreSettings();
  const activeProfessionals = useMemo(() => professionals.filter((p) => p.isActive), [professionals]);
  const [, navigate] = useLocation();

  type AgendaEntry =
    | { kind: "appointment"; date: string; time: string; appt: Appointment }
    | { kind: "order"; date: string; time: string; order: ScheduledOrderItem; itens: ScheduledOrderItem[] };

  const PENDING_ORDER_STATUSES = new Set(["novo", "confirmado", "em_preparo", "em_separacao", "pronto", "aguardando_retirada", "avisou_motoboy", "enviado"]);

  const dayAppointments = useMemo<AgendaEntry[]>(() => {
    const apptEntries: AgendaEntry[] = appointments.map((a) => ({ kind: "appointment", date: a.date, time: a.time, appt: a }));
    // Uma entrada por PEDIDO no mesmo dia e horário (data única no carrinho, 05/10/2026): 5 itens do mesmo pedido
    // eram 5 cartões iguais no mesmo horário. Itens com datas diferentes continuam em entradas separadas.
    const porPedido = new Map<string, ScheduledOrderItem[]>();
    const horarios = horarioDosTurnos((storeSettings as any)?.orderShiftHours, (storeSettings as any)?.businessHours);
    for (const o of scheduledOrders) {
      const k = `${o.orderId}|${o.scheduledDate}|${o.scheduledTime ?? ""}|${o.scheduledTime ? "" : o.scheduledShift ?? ""}`;
      const lista = porPedido.get(k);
      if (lista) lista.push(o);
      else porPedido.set(k, [o]);
    }
    // Ordem no dia (12/10/2026): sem horário abre o dia; o turno entra na hora em que começa.
    const orderEntries: AgendaEntry[] = [...porPedido.values()].map((itens) => ({ kind: "order", date: itens[0]!.scheduledDate, time: ordemNoDia(itens[0]!.scheduledTime, itens[0]!.scheduledShift, horarios), order: itens[0]!, itens }));
    let combined: AgendaEntry[] = [...apptEntries, ...orderEntries];
    if (filterStatus) {
      combined = combined.filter((e) =>
        e.kind === "appointment" ? e.appt.status === filterStatus : (filterStatus === "agendado" && PENDING_ORDER_STATUSES.has(e.order.orderStatus))
      );
    } else {
      combined = combined.filter((e) => e.date === selectedDay);
    }
    if (filterProfessionalId) combined = combined.filter((e) => e.kind === "appointment" && e.appt.professionalId === filterProfessionalId);
    if (filterServiceId) combined = combined.filter((e) => e.kind === "appointment" && e.appt.service?.id === filterServiceId);
    return combined.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  }, [appointments, scheduledOrders, selectedDay, filterProfessionalId, filterServiceId, filterStatus, storeSettings]);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [createForm, setCreateForm] = useState({ serviceId: "", professionalId: "", date: "", time: "", notes: "", customerId: "" });
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerResult | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerPhone, setNewCustomerPhone] = useState("");
  const [newCustomerDdi, setNewCustomerDdi] = useState("55");
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const customerSearchRef = useRef<HTMLDivElement>(null);

  const { data: customerResults = [] } = useCustomerSearch(customerSearch);

  const filteredServicesForModal = useMemo(() => {
    if (!createForm.professionalId) return services;
    const prof = activeProfessionals.find((p) => p.id === createForm.professionalId);
    if (!prof) return [];
    return services.filter((s) => {
      const sched = scheduleForService(prof, s.id);
      return !!sched && Object.values(sched).some((d) => d.enabled);
    });
  }, [createForm.professionalId, services, activeProfessionals]);

  const filteredProfessionalsForModal = useMemo(() => {
    if (!createForm.serviceId) return activeProfessionals;
    return activeProfessionals.filter((p) => {
      const sched = scheduleForService(p, createForm.serviceId);
      return !!sched && Object.values(sched).some((d) => d.enabled);
    });
  }, [createForm.serviceId, activeProfessionals]);

  const availableDatesForModal = useMemo(() => {
    if (!createForm.serviceId) return null;
    if (createForm.professionalId) {
      const prof = activeProfessionals.find((p) => p.id === createForm.professionalId);
      if (!prof) return [];
      const sched = scheduleForService(prof, createForm.serviceId);
      if (!sched) return [];
      return dayKeys.map((dk, i) => (sched[dk]?.enabled ? i : -1)).filter((i) => i >= 0);
    }
    return null;
  }, [createForm.serviceId, createForm.professionalId, activeProfessionals]);

  const isDateAvailable = (dateStr: string) => {
    if (!availableDatesForModal) return true;
    const dow = new Date(dateStr + "T12:00:00").getDay();
    return availableDatesForModal.includes(dow);
  };

  const { data: adminSlotsData, isLoading: loadingAdminSlots } = useAdminSlots(
    createForm.date,
    createForm.serviceId,
    createForm.professionalId,
  );

  useEffect(() => {
    if (createForm.serviceId && filteredServicesForModal.length > 0 && !filteredServicesForModal.find((s) => s.id === createForm.serviceId)) {
      setCreateForm((f) => ({ ...f, serviceId: filteredServicesForModal[0]!.id, time: "" }));
    }
  }, [filteredServicesForModal, createForm.serviceId]);

  // Serviço com profissional cadastrada: a escolha é entre elas, a primeira já
  // vem marcada (decisão do Pedro, 08/09/2026). "Sem profissional" só existe
  // quando nenhuma atende o serviço — a grade própria da loja que ele lê está
  // vazia na base inteira, então ele só produzia "Nenhum horário".
  useEffect(() => {
    const first = filteredProfessionalsForModal[0]?.id ?? "";
    const atual = createForm.professionalId;
    const valido = !!atual && filteredProfessionalsForModal.some((p) => p.id === atual);
    if (first && !valido) {
      setCreateForm((f) => ({ ...f, professionalId: first, time: "" }));
    } else if (!first && atual) {
      setCreateForm((f) => ({ ...f, professionalId: "", time: "" }));
    }
  }, [filteredProfessionalsForModal, createForm.professionalId]);


  useEffect(() => {
    setCreateForm((f) => ({ ...f, time: "" }));
  }, [createForm.date, createForm.serviceId, createForm.professionalId]);

  const prevWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };

  const nextWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };

  const formatDateBR = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  };

  const sendConfirmationWhatsApp = (appts: Appointment[]) => {
    const first = appts[0];
    if (!first?.customer?.whatsappNumber) return;
    const phone = waPhone(first.customer.whatsappNumber);
    let msg: string;
    if (appts.length === 1) {
      const appt = appts[0];
      const template = storeSettings?.appointmentMessageTemplate ||
        "Olá {cliente_nome}! Seu agendamento foi confirmado:\n\nServiço: {servico}\nData: {data}\nHorário: {hora}\nDuração: {duracao}\nValor: {preco}\n\nAté lá!";
      msg = template
        .replace(/\{cliente_nome\}/g, appt.customer?.firstName || "")
        .replace(/\{servico\}/g, appt.service?.name || "")
        .replace(/\{data\}/g, formatDateBR(appt.date))
        .replace(/\{hora\}/g, appt.time?.slice(0, 5) || "")
        .replace(/\{duracao\}/g, `${appt.durationMinutes}min`)
        .replace(/\{preco\}/g, formatCurrency(Number(appt.service?.price || 0)))
        .replace(/\{profissional\}/g, appt.professional?.name || "")
        .replace(/\{loja_nome\}/g, "");
    } else {
      const sorted = [...appts].sort((a, b) =>
        a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date)
      );
      const total = sorted.reduce((sum, a) => sum + Number(a.service?.price || 0), 0);
      msg = `Olá ${first.customer.firstName || ""}! Seus agendamentos foram confirmados:\n`;
      sorted.forEach((appt, i) => {
        msg += `\n*${i + 1}) ${appt.service?.name || "Serviço"}*\n`;
        msg += `Data: ${formatDateBR(appt.date)}\n`;
        msg += `Horário: ${appt.time?.slice(0, 5) || ""}\n`;
        if (appt.professional?.name) msg += `Profissional: ${appt.professional.name}\n`;
        msg += `Duração: ${appt.durationMinutes}min\n`;
        msg += `Valor: ${formatCurrency(Number(appt.service?.price || 0))}\n`;
      });
      msg += `\n*Valor total:* ${formatCurrency(total)}\n\nAté lá!`;
    }
    abreWhatsApp(`https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(msg)}`);
  };

  const confirmAndNotify = async (appt: Appointment) => {
    const customerId = appt.customer?.id;
    const pendingForCustomer = customerId
      ? appointments.filter((a) => a.customer?.id === customerId && a.status === "agendado")
      : [appt];
    try {
      await Promise.all(
        pendingForCustomer.map((a) =>
          customFetch(`/api/appointments/${a.id}/status`, {
            method: "PUT",
            body: JSON.stringify({ status: "confirmado" }),
          })
        )
      );
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      toast({ title: pendingForCustomer.length > 1 ? `${pendingForCustomer.length} agendamentos confirmados!` : "Status atualizado!" });
      if (pendingForCustomer[0]?.customer?.whatsappNumber) {
        sendConfirmationWhatsApp(pendingForCustomer);
      }
    } catch {
      toast({ title: "Erro ao confirmar agendamentos", variant: "destructive" });
    }
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      customFetch(`/api/appointments/${id}/status`, { method: "PUT", body: JSON.stringify({ status }) }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      if (variables.status === "concluido" || variables.status === "cancelado" || variables.status === "nao_compareceu") {
        queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
      }
      toast({ title: "Status atualizado!" });
    },
  });

  const [showAddAnother, setShowAddAnother] = useState(false);
  const [lastCreatedCustomer, setLastCreatedCustomer] = useState<CustomerResult | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      customFetch("/api/appointments", {
        method: "POST",
        body: JSON.stringify({
          serviceId: createForm.serviceId,
          professionalId: createForm.professionalId || null,
          date: createForm.date,
          time: createForm.time,
          notes: createForm.notes || null,
          customerId: createForm.customerId || null,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["admin-slots"] });
      const customer = selectedCustomer;
      setCreateOpen(false);
      toast({ title: "Agendamento criado!" });
      if (customer) {
        setLastCreatedCustomer(customer);
        setShowAddAnother(true);
      }
    },
    onError: (err: unknown) => {
      toast({ title: "Erro", description: extractApiErrorMessage(err), variant: "destructive" });
    },
  });

  // Abre já com a primeira profissional que atende o serviço inicial; o efeito
  // acima mantém isso quando o serviço muda.
  const firstQualifiedFor = (serviceId: string) =>
    activeProfessionals.find((p) => {
      const sched = serviceId ? scheduleForService(p, serviceId) : null;
      return !!sched && Object.values(sched).some((d) => d.enabled);
    })?.id ?? "";
  const defaultProfessionalId = firstQualifiedFor(services[0]?.id || "");

  const openCreate = (date?: string) => {
    setCreateForm({
      serviceId: services[0]?.id || "",
      professionalId: defaultProfessionalId,
      date: date || selectedDay,
      time: "",
      notes: "",
      customerId: "",
    });
    setSelectedCustomer(null);
    setCustomerSearch("");
    setShowNewCustomerForm(false);
    setNewCustomerName("");
    setNewCustomerPhone("");
    setNewCustomerDdi("55");
    setCreateOpen(true);
  };

  const openCreateForCustomer = (customer: CustomerResult) => {
    setCreateForm({
      serviceId: services[0]?.id || "",
      professionalId: defaultProfessionalId,
      date: selectedDay,
      time: "",
      notes: "",
      customerId: customer.id,
    });
    setSelectedCustomer(customer);
    setCustomerSearch("");
    setShowNewCustomerForm(false);
    setNewCustomerName("");
    setNewCustomerPhone("");
    setNewCustomerDdi("55");
    setShowAddAnother(false);
    setCreateOpen(true);
  };

  const handleSelectCustomer = (c: CustomerResult) => {
    setSelectedCustomer(c);
    setCreateForm((f) => ({ ...f, customerId: c.id }));
    setCustomerSearch("");
    setShowCustomerDropdown(false);
    setShowNewCustomerForm(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCreateForm((f) => ({ ...f, customerId: "" }));
    setCustomerSearch("");
    setShowNewCustomerForm(false);
    setNewCustomerName("");
    setNewCustomerPhone("");
    setNewCustomerDdi("55");
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim() || !newCustomerPhone.trim()) {
      toast({ title: "Nome e WhatsApp são obrigatórios", variant: "destructive" });
      return;
    }
    setCreatingCustomer(true);
    try {
      const customer: CustomerResult = await customFetch("/api/customers", {
        method: "POST",
        body: JSON.stringify({ firstName: newCustomerName.trim(), whatsappNumber: newCustomerPhone.replace(/\D/g, ""), ddi: newCustomerDdi }),
      });
      queryClient.invalidateQueries({ queryKey: ["customers-search"] });
      handleSelectCustomer(customer);
      toast({ title: "Cliente criado!" });
    } catch (err) {
      toast({ title: "Erro ao criar cliente", description: extractApiErrorMessage(err), variant: "destructive" });
    } finally {
      setCreatingCustomer(false);
    }
  };

  const todayStr = formatDate(new Date());
  const todayCount = appointments.filter((a) => a.date === todayStr && !["cancelado", "nao_compareceu"].includes(a.status)).length;
  const hasFilters = !!filterProfessionalId || !!filterServiceId;

  const { user } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const agendaUrl = user?.store?.slug ? `${origin}${basePath}/agendar/${user.store.slug}` : "";

  // A data do «Novo agendamento» (03/11/2026, Mariabonim_beauty: «não consigo agendar para dezembro»): era um select com
  // os próximos 14 dias. Agora o calendário do aparelho, de hoje em diante, sem limite (o link público também não tem).
  // Dia em que a profissional não atende fica escolhido, com o aviso, e o servidor não dá horário nele.
  const hojeDaAgenda = formatDate(new Date());
  const diasQueAtende = availableDatesForModal && availableDatesForModal.length < 7 ? availableDatesForModal.map((i) => dayNames[i]).join(", ") : null;

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {agendaUrl && (
        <PublicLinkCard
          url={agendaUrl}
          label="Link da Agenda:"
          icon={<CalendarDays className="w-4 h-4 text-secondary" />}
          shareLabel="nossa agenda de agendamentos"
        />
      )}
      {showFilters && (
        <Card className="p-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex flex-wrap items-end gap-4">
            {activeProfessionals.length > 0 && (
              <div className="space-y-1.5 flex-1 min-w-[180px]">
                <Label className="text-xs">Profissional</Label>
                <select className="w-full border rounded-md p-2 text-sm bg-background" value={filterProfessionalId} onChange={(e) => setFilterProfessionalId(e.target.value)}>
                  <option value="">Todos</option>
                  {activeProfessionals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div className="space-y-1.5 flex-1 min-w-[180px]">
              <Label className="text-xs">Serviço</Label>
              <select className="w-full border rounded-md p-2 text-sm bg-background" value={filterServiceId} onChange={(e) => setFilterServiceId(e.target.value)}>
                <option value="">Todos</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterProfessionalId(""); setFilterServiceId(""); }}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 shrink-0 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
          <div className="min-w-0"><p className="text-xl font-bold">{todayCount}</p><p className="text-xs text-muted-foreground">Agendamentos Hoje</p></div>
        </Card>
        <Card
          className={`p-4 flex items-center gap-3 cursor-pointer transition-all ${filterStatus === "agendado" ? "ring-2 ring-blue-500 bg-blue-50" : "hover:bg-muted/50"}`}
          onClick={() => setFilterStatus((prev) => prev === "agendado" ? "" : "agendado")}
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center"><Clock className="w-5 h-5" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-xl font-bold">{appointments.filter((a) => a.status === "agendado").length}</p>
            <p className="text-xs text-muted-foreground">Pendentes na Semana</p>
          </div>
          {filterStatus === "agendado" && <X className="w-4 h-4 text-blue-500 shrink-0" />}
        </Card>
      </div>
      <div className="flex gap-2">
        <Button variant={hasFilters ? "default" : "outline"} onClick={() => setShowFilters(!showFilters)} className="flex-1">
          <Filter className="w-4 h-4 mr-2" /> Filtros {hasFilters && <span className="ml-1 bg-white/20 text-xs px-1.5 rounded-full">!</span>}
        </Button>
        <Button onClick={() => openCreate()} className="flex-1 shadow-md shadow-primary/20">
          <Plus className="w-4 h-4 mr-2" /> Agendamento
        </Button>
      </div>
      <Card className="overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={prevWeek}><ChevronLeft className="w-5 h-5" /></Button>
          <div className="flex gap-1 sm:gap-2 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {weekDates.map((d) => {
              const ds = formatDate(d);
              const hasAppts = appointments.some((a) => a.date === ds);
              const isToday = ds === todayStr;
              const isSelected = !filterStatus && ds === selectedDay;
              return (
                <button
                  key={ds}
                  data-testid={`agenda-dia-${ds}`}
                  onClick={() => setSelectedDay(ds)}
                  className={`flex flex-col items-center px-2 sm:px-3 py-2 rounded-xl transition-all min-w-[44px] ${
                    isSelected ? "bg-primary text-primary-foreground shadow-md" : isToday ? "bg-primary/10 text-primary" : "hover:bg-muted"
                  }`}
                >
                  <span className="text-[10px] font-medium uppercase">{dayNames[d.getDay()]}</span>
                  <span className="text-lg font-bold">{d.getDate()}</span>
                  {hasAppts && <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${isSelected ? "bg-primary-foreground" : "bg-primary"}`} />}
                </button>
              );
            })}
          </div>
          <Button variant="ghost" size="icon" onClick={nextWeek}><ChevronRight className="w-5 h-5" /></Button>
        </div>

        {isLoading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : dayAppointments.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">{filterStatus ? "Nenhum agendamento pendente na semana" : "Nenhum agendamento neste dia"}</p>
            <Button variant="outline" className="mt-4" onClick={() => openCreate(selectedDay)}>
              <Plus className="w-4 h-4 mr-2" /> Criar agendamento
            </Button>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {dayAppointments.map((entry) => entry.kind === "order" ? (
              <div
                key={`order-${entry.order.orderId}-${entry.date}-${entry.time}`}
                className="p-4 rounded-lg border border-border/50 shadow-sm hover:shadow-md hover:bg-primary/5 transition-all cursor-pointer bg-primary/[0.03] border-l-4 border-l-primary"
                onClick={() => navigate("/pedidos?status=encomendas")}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Package className="w-3 h-3" /> Encomenda
                      </span>
                      {entry.itens.length === 1 && (
                        <p className="font-semibold text-base sm:text-lg leading-snug break-words">{entry.order.quantity}× {entry.order.productName}</p>
                      )}
                    </div>
                    {entry.itens.length > 1 && (
                      <div className="space-y-0.5" data-testid="agenda-itens-do-pedido">
                        {entry.itens.map((it) => (
                          <p key={it.itemId} className="font-semibold text-base leading-snug break-words">{it.quantity}× {it.productName}</p>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-base text-muted-foreground">
                      <User className="w-4 h-4 shrink-0" />
                      <span className="truncate">{entry.order.customer?.firstName || "Cliente"}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="font-semibold text-foreground text-base">{formatCurrency(entry.itens.reduce((soma, it) => soma + Number(it.price) * Number(it.quantity || "1"), 0))}</span>
                  </div>
                </div>
                <div className="self-center flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground pt-2">
                  <CalendarDays className="w-4 h-4 shrink-0" />
                  <span>{entry.date.split("-").reverse().join("/")}</span>
                  {entry.order.scheduledTime ? (
                    <>
                      <span className="text-muted-foreground/50">•</span>
                      <Clock className="w-4 h-4 shrink-0" />
                      <span>{entry.order.scheduledTime.slice(0, 5)}</span>
                    </>
                  ) : ehTurno(entry.order.scheduledShift) ? (
                    <>
                      <span className="text-muted-foreground/50">•</span>
                      <Clock className="w-4 h-4 shrink-0" />
                      <span className="font-semibold text-foreground" data-testid="agenda-turno">{NOME_DO_TURNO[entry.order.scheduledShift]}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-muted-foreground/50">•</span>
                      <span className="font-semibold text-foreground" data-testid="agenda-sem-horario">Sem horário</span>
                    </>
                  )}
                  <span className="text-muted-foreground/50">•</span>
                  <span>{rotuloDaModalidade(entry.order.deliveryMode)}</span>
                </div>
              </div>
            ) : (
              (() => { const appt = entry.appt; return (
              <div key={appt.id} className="p-4 rounded-lg border border-border/50 shadow-sm hover:shadow-md hover:bg-muted/30 transition-all">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="font-semibold text-base sm:text-lg leading-snug break-words">{appt.service?.name || "Serviço"}</p>
                    <div>
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[appt.status] || "bg-muted"}`}>
                        {statusLabels[appt.status] || appt.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-base text-muted-foreground">
                      <User className="w-4 h-4 shrink-0" />
                      <span className="truncate">{appt.customer?.firstName || "Cliente não informado"}</span>
                    </div>
                    {appt.professional && (
                      <div className="flex items-center gap-1.5 text-base text-muted-foreground">
                        <UserRound className="w-4 h-4 shrink-0" />
                        <span className="truncate">{appt.professional.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {appt.status === "agendado" && (
                      <div className="flex flex-col gap-1.5 w-[110px]">
                        <Button size="sm" variant="outline" className="h-9 w-full text-green-600 border-green-200 hover:bg-green-50" onClick={() => confirmAndNotify(appt)}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Confirmar
                        </Button>
                        <Button size="sm" variant="outline" className="h-9 w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => statusMutation.mutate({ id: appt.id, status: "cancelado" })}>
                          <XCircle className="w-4 h-4 mr-1" /> Cancelar
                        </Button>
                      </div>
                    )}
                    {appt.status === "confirmado" && (
                      <div className="flex flex-col gap-1.5 w-[110px]">
                        <Button size="sm" variant="outline" className="h-9 w-full text-green-600 border-green-200 hover:bg-green-50" onClick={() => statusMutation.mutate({ id: appt.id, status: "concluido" })}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Concluir
                        </Button>
                        <Button size="sm" variant="outline" className="h-9 w-full text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => statusMutation.mutate({ id: appt.id, status: "nao_compareceu" })}>
                          <AlertTriangle className="w-4 h-4 mr-1" /> Faltou
                        </Button>
                      </div>
                    )}
                    {appt.service && <span className="font-semibold text-foreground text-[17px] mr-[16px]">{formatCurrency(Number(appt.service.price))}</span>}
                  </div>
                </div>
                <div className="self-center flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground pt-2">
                  <CalendarDays className="w-4 h-4 shrink-0" />
                  <span>{appt.date.split("-").reverse().join("/")}</span>
                  <span className="text-muted-foreground/50">•</span>
                  <Clock className="w-4 h-4 shrink-0" />
                  <span>{appt.time.slice(0, 5)}</span>
                  <span className="text-muted-foreground/50">•</span>
                  <span>{appt.durationMinutes}min</span>
                </div>
              </div>
              ); })()
            ))}
          </div>
        )}
      </Card>
      <Dialog open={isCreateOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo Agendamento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Serviço</Label>
              <select
                className="w-full border rounded-md p-2 text-sm bg-background"
                value={createForm.serviceId}
                onChange={(e) => setCreateForm({ ...createForm, serviceId: e.target.value, time: "" })}
              >
                <option value="">Selecione um serviço</option>
                {filteredServicesForModal.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} - {formatCurrency(Number(s.price))} ({s.durationMinutes}min)</option>
                ))}
              </select>
            </div>

            {activeProfessionals.length > 0 && (
              <div className="space-y-2">
                <Label>Profissional</Label>
                <select
                  className="w-full border rounded-md p-2 text-sm bg-background"
                  value={createForm.professionalId}
                  onChange={(e) => setCreateForm({ ...createForm, professionalId: e.target.value, time: "" })}
                >
                  {filteredProfessionalsForModal.length === 0 && <option value="">Sem profissional</option>}
                  {filteredProfessionalsForModal.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Data</Label>
              <DataBr
                value={createForm.date}
                min={hojeDaAgenda}
                onChange={(v) => setCreateForm({ ...createForm, date: v, time: "" })}
                data-testid="agenda-novo-data"
              />
              {createForm.date && !isDateAvailable(createForm.date) ? (
                <p className="text-xs text-red-500" data-testid="agenda-novo-data-nao-atende">Não atende neste dia da semana. Escolha outra data{diasQueAtende ? ` (atende: ${diasQueAtende})` : ""}.</p>
              ) : diasQueAtende ? (
                <p className="text-xs text-muted-foreground" data-testid="agenda-novo-data-dias">Atende: {diasQueAtende}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>Horário</Label>
              {!createForm.date || !createForm.serviceId ? (
                <p className="text-sm text-muted-foreground">Selecione serviço e data primeiro</p>
              ) : !adminSlotsData?.slots.length ? (
                <p className="text-sm text-red-500">{loadingAdminSlots ? "Buscando horários..." : "Nenhum horário disponível nesta data"}</p>
              ) : (
                <div className="grid grid-cols-4 gap-1.5">
                  {adminSlotsData.slots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setCreateForm({ ...createForm, time: slot })}
                      className={`py-2 rounded-lg text-sm font-medium transition-all ${
                        createForm.time === slot
                          ? "bg-primary text-primary-foreground shadow"
                          : "bg-muted/50 hover:bg-muted text-foreground"
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Customer selection */}
            <div className="space-y-2">
              <Label>Cliente</Label>
              {selectedCustomer ? (
                <div className="flex items-center gap-2 rounded-md border px-3 py-2 bg-muted/30">
                  <UserRound className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{selectedCustomer.firstName}</p>
                    {selectedCustomer.whatsappNumber && (
                      <p className="text-xs text-muted-foreground">{formatWhatsApp(selectedCustomer.whatsappNumber)}</p>
                    )}
                  </div>
                  <button type="button" onClick={handleClearCustomer} className="text-muted-foreground hover:text-foreground shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : showNewCustomerForm ? (
                <div className="rounded-md border p-3 space-y-2 bg-muted/20">
                  <p className="text-sm font-medium">Novo cliente</p>
                  <Input
                    placeholder="Nome *"
                    className="bg-card"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                  />
                  <WhatsAppInput
                    value={newCustomerPhone}
                    onChange={setNewCustomerPhone}
                    ddi={newCustomerDdi}
                    onDdiChange={setNewCustomerDdi}
                    className="bg-card"
                  />
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowNewCustomerForm(false)} className="flex-1">Cancelar</Button>
                    <Button type="button" size="sm" onClick={handleCreateCustomer} disabled={creatingCustomer || !newCustomerName.trim() || !newCustomerPhone.trim()} className="flex-1">
                      {creatingCustomer ? <Loader2 className="w-3 h-3 animate-spin" /> : "Criar e Selecionar"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="relative" ref={customerSearchRef}>
                  <Input
                    placeholder="Buscar cliente..."
                    value={customerSearch}
                    onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                  {showCustomerDropdown && customerSearch.length >= 1 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md overflow-hidden">
                      {customerResults.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">Nenhum cliente encontrado</div>
                      ) : (
                        <div className="max-h-40 overflow-y-auto">
                          {customerResults.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex flex-col"
                              onClick={() => handleSelectCustomer(c)}
                            >
                              <span className="font-medium">{c.firstName}</span>
                              {c.whatsappNumber && <span className="text-xs text-muted-foreground">{formatWhatsApp(c.whatsappNumber)}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 border-t text-sm text-primary hover:bg-accent font-medium flex items-center gap-2"
                        onClick={() => { setShowCustomerDropdown(false); setShowNewCustomerForm(true); setNewCustomerName(customerSearch); setCustomerSearch(""); }}
                      >
                        <Plus className="w-3.5 h-3.5" /> Criar novo cliente
                      </button>
                    </div>
                  )}
                  {!showCustomerDropdown && (
                    <button
                      type="button"
                      className="mt-1 text-xs text-primary hover:underline flex items-center gap-1"
                      onClick={() => { setShowNewCustomerForm(true); }}
                    >
                      <Plus className="w-3 h-3" /> Criar novo cliente
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Input value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} placeholder="Opcional" />
            </div>
          </div>
          <DialogFooter className="pt-4 !flex-row gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !createForm.serviceId || !createForm.date || !createForm.time || !createForm.customerId}>
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showAddAnother} onOpenChange={setShowAddAnother}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Agendamento criado!</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Deseja adicionar outro serviço para <span className="font-semibold text-foreground">{lastCreatedCustomer?.firstName}</span>?
          </p>
          {/* Sem !flex-row: "Não, obrigado" + "Adicionar outro serviço" não
              cabem lado a lado em 390px — empilha no mobile (padrão do footer). */}
          <DialogFooter className="pt-4 gap-2">
            <Button variant="outline" onClick={() => setShowAddAnother(false)}>Não, obrigado</Button>
            <Button onClick={() => { if (lastCreatedCustomer) openCreateForCustomer(lastCreatedCustomer); }}>
              <Plus className="w-4 h-4 mr-1" /> Adicionar outro serviço
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
